import { CROCKFORD_ALPHABET } from './crockford.js';
import { checksum4, extractPayload } from './payload.js';
/**
 * 订单码（自重码，契约 §20）。
 *
 * 与地点码/商品码的**根本差别**：它变长、可多页，且必须能**完全离线**重建商品清单。
 * 因此这里不用「定长 15 位」那套约束，改用分隔符文本 + 每页自带完整头部。
 *
 * 四条设计要点：
 *   ① 载荷**并非**全部落在 QR 字母数字模式字符集（`0-9 A-Z $%*+-./:`）：分隔符 `|` 与 `;`
 *      不在该集合内，且 `shelfCode` 允许**小写与 CJK**（`SHELF_CODE_PATTERN`）—— 这部分会
 *      整段落进**字节模式**。所以容量判定的权威量是**模块数**而非字符数（计划 §2.1：
 *      任一页模块数 ≤ 117 ⇒ ≤ 468 px），`MAX_CHARS_PER_CODE` 只是粗粒度护栏；
 *      Task 21/22 渲染时必须按真实模块数算宽度，不能按字符数换算。
 *   ② `unitPriceCents` 存**实付单价**，收银端即使完全忽略 `flags` 也不会收错钱；
 *   ③ 校验位复用 `checksum4` —— 全系统只有一套校验算法（见 `payload.ts:100-113` 的声明）；
 *   ④ 解码用 `extractPayload`（**不做大小写变换**）而不是 `normalizeToken`：CRC 覆盖原文，
 *      大写化整串会让小写货架位的码解不开自己（品质审查 C1，见 `payload.ts` 的说明）。
 */
export const ORDER_CODE_PREFIX = 'CUITO1';
/**
 * 契约 §20.3 的**字段总数**口径：前缀 + 8 个头字段 + `lines` + CRC = 12。
 *
 * 它与 `buildHeaderText` 里说的「头部」（不含 `lines` 与 CRC 的那 11 个字段）**不是一回事** ——
 * 解码时 `fields.length !== ORDER_CODE_HEADER_FIELDS` 判损用的正是这个总数口径。
 * （品质审查 M2：同一个名字曾同时表示两个口径。）
 */
export const ORDER_CODE_HEADER_FIELDS = 12;
export const ORDER_CODE_LINE_FIELDS = 6;
export const MAX_LINES_PER_CODE = 20;
export const MAX_CHARS_PER_CODE = 1200;
/**
 * 最大页数 ⇒ 整单上限 120 行。
 *
 * ⚠️ **必须 ≤ 9**：编码时的字符收缩检查用 `buildHeaderText(input, 1, pages, chunk)` 试算长度
 * （品质审查 M3），页码取**代表值 1**（1 位）；若 `MAX_PAGES ≥ 10`，2 位页码会让真实长度比
 * 试算值多 1 字符，收缩判断因此可能偏乐观。当前 6 满足该前提。
 */
export const MAX_PAGES = 6;
export const MAX_TOTAL_LINES = MAX_LINES_PER_CODE * MAX_PAGES;
/**
 * 离线接单时限（小时）——契约 §20.6 的第 8 个冻结常量（之前是 7 个）。
 *
 * 服务端的离线闸门与收银端的离线分支**读同一个符号**：只有一处定义才不会漂移。
 * 服务端允许环境变量覆盖（验收造「25 小时前签发的码」用，不 sleep 真实时间）；
 * 客户端只读常量（浏览器里没有那个环境变量）。
 */
export const OFFLINE_ORDER_TTL_HOURS = 24;
const SHELF_CODE_PATTERN = /^[A-Za-z0-9\u4e00-\u9fa5-]{1,16}$/;
const SHORT_CODE_PATTERN = /^\d{4,6}$/;
const DIGITS_PATTERN = /^\d+$/;
function assertCrockford(value, length, subject) {
    if (value.length !== length)
        throw new Error(`ORDER_CODE_INVALID_${subject}_LENGTH`);
    for (const ch of value) {
        if (!CROCKFORD_ALPHABET.includes(ch))
            throw new Error(`ORDER_CODE_INVALID_${subject}_CHARSET`);
    }
}
function serializeLine(line) {
    const flags = (line.inPromotion ? 1 : 0) | (line.refunded ? 2 : 0);
    return [line.lineNo, line.productCode, line.qty, line.shelfCode, line.unitPriceCents, flags].join(':');
}
function deserializeLine(raw) {
    const parts = raw.split(':');
    if (parts.length !== ORDER_CODE_LINE_FIELDS)
        return null;
    const [lineNo, productCode, qty, shelfCode, unitPriceCents, flags] = parts;
    if (![lineNo, qty, unitPriceCents, flags].every((value) => DIGITS_PATTERN.test(value)))
        return null;
    if (shelfCode === '' || !SHELF_CODE_PATTERN.test(shelfCode))
        return null;
    if (productCode.length !== 8)
        return null;
    for (const ch of productCode)
        if (!CROCKFORD_ALPHABET.includes(ch))
            return null;
    const flagBits = Number(flags);
    return {
        lineNo: Number(lineNo),
        productCode,
        qty: Number(qty),
        shelfCode,
        unitPriceCents: Number(unitPriceCents),
        inPromotion: (flagBits & 1) === 1,
        refunded: (flagBits & 2) === 2,
    };
}
/** 头部（不含 `lines` 与 CRC）。**每页都带完整头部**：任意一页被单独扫到都能知道整单规模 */
function buildHeaderText(input, page, pages, lines) {
    return [
        ORDER_CODE_PREFIX,
        page,
        pages,
        input.orderNo,
        input.shortCode,
        input.orderId.replace(/-/g, '').slice(0, 8).toUpperCase(),
        input.lines.length, // 整单行数，跨页恒定
        input.totalAmountCents,
        input.issuedAt,
        input.locationCode,
        lines.map(serializeLine).join(';'),
    ].join('|');
}
function withChecksum(headerText) {
    return `${headerText}|${checksum4(headerText)}`;
}
/**
 * 编码器必须与解码器对同一入参一致：载荷里写的是**十进制文本**，而 JS 会把 `|v| ≥ 1e21` 打成
 * `1e+21`（`DIGITS_PATTERN` 不匹配）⇒ 那种载荷**我们自己都解不开**。这里直接拒绝，而不是产出
 * 坏载荷 —— 「编码器永远不产出自己的解码器读不了的载荷」是一条应当成立的不变量（与 C1 同族：
 * 两者都是「编码端与解码端对同一输入不一致」）。
 *
 * 现实订单够不到（`qty` 是数据库整数、金额 ≤ 999,999,999 分、`issuedAt` 是 10 位 Unix 秒），
 * 守卫防的是未来的批量/导入调用方。
 */
function assertPlainDigits(value, subject) {
    if (!DIGITS_PATTERN.test(String(value)))
        throw new Error(`ORDER_CODE_INVALID_${subject}`);
}
function validateInput(input) {
    assertCrockford(input.orderNo, 10, 'ORDER_NO');
    if (!SHORT_CODE_PATTERN.test(input.shortCode))
        throw new Error('ORDER_CODE_INVALID_SHORT_CODE');
    assertCrockford(input.locationCode, 8, 'LOCATION_CODE');
    if (!Number.isInteger(input.issuedAt) || input.issuedAt <= 0)
        throw new Error('ORDER_CODE_INVALID_ISSUED_AT');
    assertPlainDigits(input.issuedAt, 'ISSUED_AT');
    // 金额下界是 **0（含）**，不是 `> 0`：**全行缺货**的订单（Phase 5 的
    // `POST /orders/:id/short` 把每行的 `shortQty` 标到 `quantity`）应付就是 0 分，
    // 而它仍然是一张**合法订单** —— 顾客仍然要拿到能离线解析的码（否则收银端与顾客端
    // 对同一张单各说各话）。写成 `> 0` 会把「金额为 0」这个正常业务事实变成 500
    // （实测：全行缺货后 `POST /orders/:id/order-code` 500，Task 3 的 ⑬b 用例钉住它）。
    //
    // **负数仍然拒绝**：那种值只可能是坏数据。`ORDER_CODE_INVALID_TOTAL_AMOUNT` 因此保留
    // —— 它现在覆盖负数、非整数与下面 `assertPlainDigits` 的 `≥ 1e21` 十进制守卫，
    // 只是**不再**由 0 触发。
    if (!Number.isInteger(input.totalAmountCents) || input.totalAmountCents < 0) {
        throw new Error('ORDER_CODE_INVALID_TOTAL_AMOUNT');
    }
    assertPlainDigits(input.totalAmountCents, 'TOTAL_AMOUNT');
    if (input.lines.length === 0)
        throw new Error('ORDER_CODE_INVALID_LINE_COUNT');
    if (input.lines.length > MAX_TOTAL_LINES)
        throw new Error('ORDER_CODE_TOO_MANY_LINES');
    input.lines.forEach((line, index) => {
        if (line.lineNo !== index + 1)
            throw new Error('ORDER_CODE_INVALID_LINE_NO');
        if (line.productCode.length !== 8)
            throw new Error('ORDER_CODE_INVALID_LINE');
        if (!Number.isInteger(line.qty) || line.qty < 1)
            throw new Error('ORDER_CODE_INVALID_LINE');
        assertPlainDigits(line.qty, 'LINE');
        if (!Number.isInteger(line.unitPriceCents) || line.unitPriceCents < 0)
            throw new Error('ORDER_CODE_INVALID_LINE');
        assertPlainDigits(line.unitPriceCents, 'LINE');
        if (!SHELF_CODE_PATTERN.test(line.shelfCode))
            throw new Error('ORDER_CODE_INVALID_LINE');
    });
}
/**
 * 编码：整单 → 若干页。**任一行都不跨页**，`lineNo` 保持输入顺序。
 *
 * 分页两步：先按 `MAX_LINES_PER_CODE` 切，再检查每页字符数；超限就把每页行数**减半**重排
 * （20 → 10 → 5 → 2 → 1，`Math.floor` 的整数序列 —— 品质审查 M1：注释曾误写成含 3）。
 * 减半而不是「只拆超长的那一页」，是为了让所有页行数一致 ——
 * 顾客翻页时每页码密度相同，收银端扫任意一页的识别难度也一致。
 */
export function encodeOrderCode(input) {
    validateInput(input);
    let perPage = MAX_LINES_PER_CODE;
    let chunks;
    for (;;) {
        chunks = [];
        for (let index = 0; index < input.lines.length; index += perPage) {
            chunks.push(input.lines.slice(index, index + perPage));
        }
        const pages = chunks.length;
        const tooLong = chunks.some((chunk) => withChecksum(buildHeaderText(input, 1, pages, chunk)).length > MAX_CHARS_PER_CODE);
        if (!tooLong)
            break;
        if (perPage === 1)
            throw new Error('ORDER_CODE_LINE_TOO_LONG');
        perPage = Math.max(1, Math.floor(perPage / 2));
    }
    const pages = chunks.length;
    if (pages > MAX_PAGES)
        throw new Error('ORDER_CODE_TOO_MANY_LINES');
    return chunks.map((chunk, index) => {
        const payload = withChecksum(buildHeaderText(input, index + 1, pages, chunk));
        return { page: index + 1, pages, lineCount: chunk.length, charCount: payload.length, payload };
    });
}
/** 解码单页。**永不抛错**：坏码是正常输入（顾客会扫错码、码会磨损），返回值里给原因 */
export function decodeOrderCode(payload) {
    if (typeof payload !== 'string')
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    // 用 `extractPayload` 而**不是** `normalizeToken`（品质审查 C1）：后者会把整串大写化，
    // 于是下面重算 CRC 时用的是「大写化后的串」，而编码时的 CRC 覆盖原文 —— 只要货架位标识
    // 含小写（`a3`）就会恒判 `ORDER_CODE_INVALID`（自己编的码自己解不开）；CJK 因为
    // `toUpperCase` 不动它而侥幸通过。这里保持原文，返回的 `shelfCode` 也就与快照逐字一致。
    const token = extractPayload(payload);
    const fields = token.split('|');
    if (fields.length !== ORDER_CODE_HEADER_FIELDS)
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    const [prefix, pageText, pagesText, orderNo, shortCode, orderId8, lineCountText, totalText, issuedAtText, locationCode, linesText, crc,] = fields;
    if (prefix !== ORDER_CODE_PREFIX)
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    if (![pageText, pagesText, lineCountText, totalText, issuedAtText].every((value) => DIGITS_PATTERN.test(value))) {
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    }
    if (!SHORT_CODE_PATTERN.test(shortCode))
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    if (orderNo.length !== 10 || locationCode.length !== 8 || orderId8.length !== 8) {
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    }
    for (const ch of orderNo)
        if (!CROCKFORD_ALPHABET.includes(ch))
            return { ok: false, reason: 'ORDER_CODE_INVALID' };
    for (const ch of locationCode)
        if (!CROCKFORD_ALPHABET.includes(ch))
            return { ok: false, reason: 'ORDER_CODE_INVALID' };
    const page = Number(pageText);
    const pages = Number(pagesText);
    const lineCount = Number(lineCountText);
    if (pages < 1 || pages > MAX_PAGES || page < 1 || page > pages)
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    if (lineCount < 1 || lineCount > MAX_TOTAL_LINES)
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    const lines = [];
    for (const raw of linesText.split(';')) {
        const line = deserializeLine(raw);
        if (!line)
            return { ok: false, reason: 'ORDER_CODE_INVALID' };
        lines.push(line);
    }
    // 行号必须落进 `1..lineCount`（品质审查 I4）：只拒「大于 lineCount」会放行 `lineNo = 0`
    // —— 实测 `0;1;2` 配 `lineCount = 3` 曾判 ok，等于**缺了第 3 行却报完整**。
    if (lines.length === 0 || lines.some((line) => line.lineNo < 1 || line.lineNo > lineCount)) {
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    }
    // CRC 放最后校验：先把结构问题与「内容损坏」分开，提示才准确
    const headerText = fields.slice(0, ORDER_CODE_HEADER_FIELDS - 1).join('|');
    if (checksum4(headerText) !== crc)
        return { ok: false, reason: 'ORDER_CODE_INVALID' };
    return {
        ok: true,
        header: {
            orderNo, shortCode, orderId8, lineCount,
            totalAmountCents: Number(totalText),
            issuedAt: Number(issuedAtText),
            locationCode,
            pages,
        },
        page: { page, pages, lineCount: lines.length, charCount: token.length, payload: token },
        lines,
    };
}
/**
 * **只**做校验位复核，不做任何其他形态校验。
 *
 * 对任何输入都不抛异常（畸形输入返回 false）—— 它是判定，不是解析器。
 * 字段数、前缀、尾部 Crockford 三者任一不成立即 false；否则重算前 11 个字段的校验位比对。
 */
export function orderCodeChecksumOk(payload) {
    if (typeof payload !== 'string')
        return false;
    const fields = payload.split('|');
    if (fields.length !== ORDER_CODE_HEADER_FIELDS)
        return false;
    if (fields[0] !== ORDER_CODE_PREFIX)
        return false;
    const crc = fields[ORDER_CODE_HEADER_FIELDS - 1];
    if (crc === undefined || !/^[0-9A-HJKMNP-TV-Z]{4}$/.test(crc))
        return false;
    const headerText = fields.slice(0, ORDER_CODE_HEADER_FIELDS - 1).join('|');
    return checksum4(headerText) === crc;
}
/**
 * 合并多页。**乱序扫、重复扫都要能用**（收银端会让顾客「把剩下的码都扫一遍」）。
 *
 * 四道校验：① 所有页的头部必须逐字段一致（否则是两张不同的订单混在一起）；
 * ② 按 `lineNo` 去重合并；③ 行号必须恰好覆盖 `1..lineCount`；④ 缺页时给出已扫行数 ——
 * 收银端要显示「已扫 X/Y 行」。
 */
export function mergeOrderCodePages(results) {
    const okPages = results.filter((result) => result.ok);
    if (okPages.length === 0)
        return { ok: false, reason: 'ORDER_CODE_INCOMPLETE', scannedLines: 0, lineCount: 0 };
    const first = okPages[0].header;
    for (const result of okPages) {
        const header = result.header;
        if (header.orderNo !== first.orderNo || header.shortCode !== first.shortCode ||
            header.orderId8 !== first.orderId8 || header.lineCount !== first.lineCount ||
            header.totalAmountCents !== first.totalAmountCents || header.issuedAt !== first.issuedAt ||
            header.locationCode !== first.locationCode || header.pages !== first.pages) {
            // 头部不一致 = 扫进了别的订单的码，按「不完整」返回而不是「完整但内容奇怪」
            return { ok: false, reason: 'ORDER_CODE_INCOMPLETE', scannedLines: 0, lineCount: first.lineCount };
        }
    }
    const byLineNo = new Map();
    for (const result of okPages) {
        for (const line of result.lines)
            if (!byLineNo.has(line.lineNo))
                byLineNo.set(line.lineNo, line);
    }
    // ③ 行号必须**恰好覆盖** `1..lineCount`（品质审查 I4）。只比 `size` 会放过「数量对但集合错」：
    // 实测 `0;1;2` 配 `lineCount = 3` 的 size 同样是 3，却缺了第 3 行。decode 侧已拒 `lineNo < 1`，
    // 这里再独立守一道 —— `mergeOrderCodePages` 是公开 API，入参不保证来自 `decodeOrderCode`。
    for (let lineNo = 1; lineNo <= first.lineCount; lineNo += 1) {
        if (!byLineNo.has(lineNo)) {
            return { ok: false, reason: 'ORDER_CODE_INCOMPLETE', scannedLines: byLineNo.size, lineCount: first.lineCount };
        }
    }
    // 反向还要挡住**越界的多余行**（例如 `1..4` 配 `lineCount = 3`）：集合恰好相等才算完整
    if (byLineNo.size !== first.lineCount) {
        return { ok: false, reason: 'ORDER_CODE_INCOMPLETE', scannedLines: byLineNo.size, lineCount: first.lineCount };
    }
    return { ok: true, header: first, lines: [...byLineNo.values()].sort((a, b) => a.lineNo - b.lineNo) };
}
//# sourceMappingURL=order-code.js.map